import React, { useState, useEffect, Suspense } from 'react';
import { lazyWithRetry as lazy } from './utils/lazyWithRetry';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import Layout from './components/Layout';
import PageErrorBoundary from './components/PageErrorBoundary';
import Register from './pages/Register';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import GoogleAuthButton from './components/GoogleAuthButton';
import './App.css';
import { LanguageProvider } from './contexts/LanguageContext';
import { HybridAuthProvider, useHybridAuth } from './contexts/HybridAuthContext';
import { NotificationModalProvider } from './contexts/NotificationModalContext';
import { VoiceAssistantProvider } from './contexts/VoiceAssistantProvider';
import { sendVoiceCommand } from './utils/voiceCommandClient';
import { speakResponse, stopSpeaking } from './utils/voiceTts';
import type { VoiceCommandRequest, VoiceCommandResponse } from './voice/types';
import './i18n';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Contacts = lazy(() => import('./pages/Contacts'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const ChartOfAccounts = lazy(() => import('./pages/ChartOfAccounts'));
const BankAccounts = lazy(() => import('./pages/BankAccounts'));
const BankTransactions = lazy(() => import('./pages/BankTransactions'));
const Inventory = lazy(() => import('./pages/Inventory'));
const JournalEntries = lazy(() => import('./pages/JournalEntries'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Reports = lazy(() => import('./pages/Reports'));
const TaxPeriods = lazy(() => import('./pages/TaxPeriods'));
const VoiceAssistant = lazy(() => import('./pages/VoiceAssistant'));
const MicrophoneTest = lazy(() => import('./pages/MicrophoneTest'));
const ImageProcessing = lazy(() => import('./pages/ImageProcessing'));
const Integrations = lazy(() => import('./pages/Integrations'));
const IntegrationCallback = lazy(() => import('./pages/IntegrationCallback'));
const Settings = lazy(() => import('./pages/Settings'));
const BulkPayments = lazy(() => import('./pages/BulkPayments'));
const Expenses = lazy(() => import('./pages/Expenses'));
const Transfers = lazy(() => import('./pages/Transfers'));
const PurchaseInvoiceView = lazy(() => import('./pages/PurchaseInvoiceView'));
const SalesInvoiceView = lazy(() => import('./pages/SalesInvoiceView'));
const AspOperationsDashboard = lazy(() => import('./pages/AspOperationsDashboard'));
const QuoteView = lazy(() => import('./pages/QuoteView'));
const Quotes = lazy(() => import('./pages/Quotes'));
const PublicInvoiceView = lazy(() => import('./pages/PublicDocumentView').then((m) => ({ default: m.PublicInvoiceView })));
const PublicQuoteView = lazy(() => import('./pages/PublicDocumentView').then((m) => ({ default: m.PublicQuoteView })));
const SuperAdmin = lazy(() => import('./pages/SuperAdmin'));
const SuperAdminOtherSettings = lazy(() => import('./pages/SuperAdminOtherSettings'));
const BusinessManagement = lazy(() => import('./pages/BusinessManagement'));

const PageLoader: React.FC<{ fullScreen?: boolean; label?: string; detail?: string }> = ({
  fullScreen = false,
  label = 'Loading page',
  detail,
}) => (
  <div
    className={`app-loader ${fullScreen ? 'app-loader--screen' : 'app-loader--page'}`}
    role="status"
    aria-busy="true"
    aria-live="polite"
  >
    <div className="app-loader__panel">
      <div className="app-loader__mark" aria-hidden="true">
        <span>EZ</span>
      </div>
      <div className="app-loader__content">
        <p className="app-loader__label">{label}</p>
        {detail && <p className="app-loader__detail">{detail}</p>}
        <div className="app-loader__bar" aria-hidden="true">
          <span />
        </div>
      </div>
    </div>
  </div>
);

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const { login, loading, loginError, authRateLimited } = useHybridAuth();
  const { t } = useTranslation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login(email, password);
  };

  if (showRegister) {
    return <Register onLogin={() => setShowRegister(false)} />;
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>{t('auth.welcome')}</h1>
          <p>{t('auth.subtitle')}</p>
        </div>

        <GoogleAuthButton mode="login" disabled={loading || authRateLimited} />

        <div className="auth-divider">
          <span>{t('auth.orContinueWithEmail')}</span>
        </div>

        <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              autoComplete="username email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('auth.email')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={t('auth.password')}
            />
          </div>

          {loginError && (
            <div className="error-message">
              {loginError}
            </div>
          )}

          <button
            type="submit"
            className="login-btn"
            disabled={loading || authRateLimited}
          >
            {loading ? t('auth.signingIn') : authRateLimited ? 'Please wait…' : t('auth.login')}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Don't have an account?{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => setShowRegister(true)}
            >
              Create one here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoggingOut } = useHybridAuth();

  if (isLoggingOut) {
    return null;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const LazyPage: React.FC<{ name: string; children: React.ReactNode }> = ({ name, children }) => (
  <PageErrorBoundary pageName={name}>
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  </PageErrorBoundary>
);

const App: React.FC = () => {
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    const checkI18nReady = () => {
      if (i18n.isInitialized) {
        setI18nReady(true);
      } else {
        setTimeout(checkI18nReady, 100);
      }
    };

    checkI18nReady();
  }, []);

  if (!i18nReady) {
    return (
      <PageLoader
        fullScreen
        label="Starting EZAccounting"
        detail="Loading language settings"
      />
    );
  }

  return (
    <LanguageProvider>
      <HybridAuthProvider>
        <NotificationModalProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
              <Route path="/view/invoice/:token" element={
                <LazyPage name="Public Invoice">
                  <PublicInvoiceView />
                </LazyPage>
              } />
              <Route path="/view/quote/:token" element={
                <LazyPage name="Public Quote">
                  <PublicQuoteView />
                </LazyPage>
              } />
              <Route path="/*" element={<AppContent />} />
            </Routes>
          </Router>
        </NotificationModalProvider>
      </HybridAuthProvider>
    </LanguageProvider>
  );
};

const AppContent: React.FC = () => {
  const { authInitialized, isAuthenticated, user, business, accounts, contacts, logout, refreshData } = useHybridAuth();

  if (!authInitialized) {
    return (
      <PageLoader
        fullScreen
        label="Preparing workspace"
        detail="Checking your saved session"
      />
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const sendCommand = async (
    request: VoiceCommandRequest,
    _signal: AbortSignal
  ): Promise<VoiceCommandResponse> => {
    const result = await sendVoiceCommand(request.voice_text, request.session_id || '', {
      isOpen: true,
      isMinimized: false,
      isRecording: false,
      messages: [],
    }, {
      command_id: request.command_id,
      capture_id: request.capture_id,
      source: request.source,
      pending_command_id: request.pending_command_id,
      confirmed: request.confirmed,
    });

    return {
      success: Boolean(result.success),
      message: result.message || '',
      command_id: result.command_id || request.command_id,
      session_id: result.session_id || request.session_id || '',
      phase: (result.phase as VoiceCommandResponse['phase']) || (result.success ? 'completed' : 'failed'),
      pending_action: result.pending_action
        ? {
            command_id: result.pending_action.command_id || result.command_id || request.command_id,
            type: 'pending',
            summary: result.pending_action.data || {},
            expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
          }
        : undefined,
      data: result.data,
      suggestions: Array.isArray(result.suggestions)
        ? result.suggestions.map(String)
        : undefined,
      error_code: result.error_code as VoiceCommandResponse['error_code'],
    };
  };

  return (
    <VoiceAssistantProvider
      sendCommand={sendCommand}
      onSpeak={(text) => { void speakResponse(text); }}
      onStopSpeak={stopSpeaking}
    >
      <Layout user={user} business={business} onLogout={logout}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <LazyPage name="Dashboard">
                <Dashboard
                  user={user}
                  business={business}
                  accounts={accounts}
                  contacts={contacts}
                  onRefresh={refreshData}
                />
              </LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/contacts" element={
            <ProtectedRoute>
              <LazyPage name="Contacts"><Contacts /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/contacts/:id" element={
            <ProtectedRoute>
              <LazyPage name="Contact Detail"><ContactDetail /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/chart-of-accounts" element={
            <ProtectedRoute>
              <LazyPage name="Chart of Accounts"><ChartOfAccounts /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/bank-accounts" element={
            <ProtectedRoute>
              <LazyPage name="Bank Accounts"><BankAccounts /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/bank-accounts/:accountId/transactions" element={
            <ProtectedRoute>
              <LazyPage name="Bank Transactions"><BankTransactions /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/inventory" element={
            <ProtectedRoute>
              <LazyPage name="Inventory"><Inventory /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/journal-entries" element={
            <ProtectedRoute>
              <LazyPage name="Journal Entries"><JournalEntries /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/invoices" element={
            <ProtectedRoute>
              <LazyPage name="Invoices"><Invoices /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/quotes" element={
            <ProtectedRoute>
              <LazyPage name="Quotes"><Quotes /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/quotes/:id" element={
            <ProtectedRoute>
              <LazyPage name="Quote"><QuoteView /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/purchase-invoices/:id" element={
            <ProtectedRoute>
              <LazyPage name="Purchase Invoice"><PurchaseInvoiceView /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/sales-invoices/:id" element={
            <ProtectedRoute>
              <LazyPage name="Sales Invoice"><SalesInvoiceView /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/asp-operations" element={
            <ProtectedRoute>
              <LazyPage name="ASP Operations"><AspOperationsDashboard /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute>
              <LazyPage name="Reports"><Reports /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/tax-periods" element={
            <ProtectedRoute>
              <LazyPage name="Tax Periods"><TaxPeriods /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/voice-assistant" element={
            <ProtectedRoute>
              <LazyPage name="Voice Assistant"><VoiceAssistant /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/microphone-test" element={
            <ProtectedRoute>
              <LazyPage name="Microphone Test"><MicrophoneTest /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/image-processing" element={
            <ProtectedRoute>
              <LazyPage name="Image Processing"><ImageProcessing /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/integrations" element={
            <ProtectedRoute>
              <LazyPage name="Integrations"><Integrations /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/integration/:service/callback" element={
            <ProtectedRoute>
              <LazyPage name="Integration Callback"><IntegrationCallback /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <LazyPage name="Settings"><Settings /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/bulk-payments" element={
            <ProtectedRoute>
              <LazyPage name="Bulk Payments"><BulkPayments /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/expenses" element={
            <ProtectedRoute>
              <LazyPage name="Expenses"><Expenses /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/transfers" element={
            <ProtectedRoute>
              <LazyPage name="Transfers"><Transfers /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/super-admin" element={
            <ProtectedRoute>
              <LazyPage name="Super Admin"><SuperAdmin /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/super-admin/settings" element={
            <ProtectedRoute>
              <LazyPage name="Super Admin Settings"><SuperAdminOtherSettings /></LazyPage>
            </ProtectedRoute>
          } />
          <Route path="/business-management" element={
            <ProtectedRoute>
              <LazyPage name="Business Management"><BusinessManagement /></LazyPage>
            </ProtectedRoute>
          } />
        </Routes>
      </Layout>
    </VoiceAssistantProvider>
  );
};

export default App;
